export const meta = {
  id: 1,
  title: 'Why React Needed Fiber: A Deep Dive into Concurrent Rendering',
  excerpt: 'Understanding the architectural shift in React 16. Why synchronous rendering became a bottleneck and how Fiber rebuilt the core algorithm to support interruptible work.',
  date: 'August 28, 2026',
  category: 'Architecture',
  tags: ['React', 'Architecture', 'Performance'],
  readingTime: '10–12 min',
  difficulty: 'Intermediate',
  isPublished: true
};

React feels incredibly responsive most of the time.

Click a button.
Type into an input.
Expand a menu.

Everything updates almost instantly.

But imagine a different scenario.

You're viewing a dashboard containing hundreds of components. A user clicks a button, and React begins rendering a large component tree. Halfway through, the user starts typing into a search field.

Which should React finish first?

Continue rendering the dashboard?

Or immediately respond to the user's input?

Before React 16, React didn't have a choice.

Once rendering began, it had to finish.

No pauses.

No interruptions.

No prioritization.

The browser had to wait until the entire component tree finished rendering before React could process anything else.

For small applications, this wasn't noticeable.

For large applications, it could lead to dropped frames, delayed interactions, and an interface that felt sluggish.

This limitation wasn't caused by the Virtual DOM.

It wasn't caused by JavaScript.

It wasn't even caused by React components.

The real limitation was React's rendering engine itself.

To solve this problem, React's team redesigned the reconciler from the ground up.

The result was Fiber.

In this article, we'll explore why Fiber was necessary, how it works internally, and why it became the foundation for modern React features like Concurrent Rendering, Suspense, and Transitions.

## React Before Fiber

To understand why Fiber exists, we first need to understand how React worked before React 16.

React's original rendering engine was known as the **Stack Reconciler**.

Its job was simple:

1. Compare the previous Virtual DOM with the new Virtual DOM.
2. Determine what changed.
3. Generate the minimal set of DOM updates.
4. Commit those updates to the browser.

The algorithm was efficient and predictable.

But it had one important characteristic.

It was **entirely synchronous**.

Once React started reconciling a component tree, the process continued until every component had been visited.

There was no opportunity to pause halfway through, process a higher-priority update, and resume later.

The JavaScript call stack dictated the execution order.

This worked well when applications were relatively small.

However, as React applications grew larger, with dashboards, data grids, editors, and complex enterprise interfaces, the limitations became increasingly apparent.

---

## The Problem Nobody Talks About

When developers discuss React performance, the conversation usually revolves around things like:

- unnecessary re-renders
- `React.memo`
- `useMemo`
- `useCallback`

While these are important, they weren't the problem that motivated React Fiber.

The real issue was much deeper.

React had no way to decide **which work was more important.**

Imagine a user interacting with your application while React is rendering a large update.

Perhaps they're:

- typing into a search box
- clicking a button
- opening a dropdown
- dragging a slider

At the same time, another part of the application is rendering hundreds of components.

From the user's perspective, their interaction should always take priority.

But before React 16, every update was treated equally.

Once reconciliation started, React had to finish the entire component tree before the browser could process new work.

The browser couldn't ask React to "pause for a moment."

React couldn't say "I'll finish this later."

Everything had to complete in one uninterrupted execution.

That wasn't just a limitation.

It fundamentally affected how responsive large applications could feel.

## Understanding the Stack Reconciler

The original React reconciler relied on JavaScript's call stack.

When a component updated, React recursively traversed the component tree.

A simplified version looked something like this:

```javascript
function render(component) {
  render(component.child1)
  render(component.child2)
  render(component.child3)
}
```

Every recursive function call was pushed onto the JavaScript call stack.

The important consequence is that JavaScript execution is synchronous.

Once a function starts executing, it continues until it returns.

That means React couldn't pause halfway through rendering.

It couldn't save its progress.

It couldn't resume later.

It simply followed the rules of the JavaScript runtime.

This made the implementation elegant and predictable.

But it also meant React had no control over scheduling.

The browser had to wait until React finished every recursive call before it could continue handling other work.

## Why Synchronous Rendering Was a Problem

To understand why this mattered, we need to remember that React doesn't run in isolation.

It runs on the browser's **main thread**.

That same thread is responsible for:

- executing JavaScript
- handling user input
- running animations
- recalculating layouts
- painting pixels to the screen

When React spent a long time reconciling a large component tree, it occupied the main thread.

During that period, the browser couldn't respond to user interactions.

Animations could stutter.

Typing could feel delayed.

Scrolling could become less responsive.

This phenomenon is commonly referred to as **blocking the main thread**.

The problem wasn't that React was "slow."

The problem was that React couldn't voluntarily yield control back to the browser.

Modern applications needed something different.

They needed a rendering engine that could pause, prioritize, and resume work when appropriate.

That requirement eventually led to Fiber.

<Callout type="deep-dive" title="Deep Dive">
React wasn't slow because of the Virtual DOM. The bottleneck was that the entire reconciliation process occupied the main thread until it completed.
</Callout>

```mermaid
sequenceDiagram
    participant User
    participant Browser
    participant React

    User->>Browser: Click Button
    Browser->>React: Start Reconciliation
    React-->>React: Render Entire Tree
    Note over React: Cannot pause
    Browser-->>User: Response delayed
```

## Enter Fiber

React's team didn't set out to build a new data structure.

They set out to build a new rendering engine.

The goal was simple in theory but difficult in practice:

> **Allow React to pause rendering, prioritize more important work, and resume exactly where it left off.**

To achieve that, React needed something the Stack Reconciler couldn't provide.

It needed control over its own execution.

Instead of relying entirely on JavaScript's call stack, React introduced its own representation of work.

That representation is called a **Fiber**.

Every component in your application now has a corresponding Fiber object.

Rather than treating reconciliation as one large, uninterrupted task, Fiber breaks rendering into many small pieces of work.

Each piece can be processed independently.

That seemingly small change unlocked capabilities that were previously impossible:

- prioritizing updates
- pausing long renders
- resuming unfinished work
- abandoning obsolete work
- preparing UI in the background

Fiber wasn't simply a faster reconciler.

It fundamentally changed **how React thinks about rendering.**

## What Exactly Is a Fiber?

One of the biggest misconceptions is that Fiber is a rendering algorithm.

It isn't.

A Fiber is simply a JavaScript object.

More specifically, it's a data structure that represents a single unit of work for a React component.

Every rendered component has its own Fiber.

If your component tree looks like this:

```text
<App>
 ├── <Navbar />
 ├── <Sidebar />
 └── <Dashboard />
```

React internally creates a corresponding Fiber tree.

```mermaid
graph TD
    A[App Fiber]
    A --> B[Navbar Fiber]
    A --> C[Sidebar Fiber]
    A --> D[Dashboard Fiber]
```

Each Fiber stores everything React needs to continue rendering later, including:

- the component type
- props
- state
- pending updates
- effect information
- relationships with other Fibers

Instead of relying on the JavaScript call stack to remember progress, React stores that information inside the Fiber itself.

That distinction is what makes interruptible rendering possible.

## Fiber as a Unit of Work

The most important idea behind Fiber isn't the object itself.

It's what that object represents.

Each Fiber corresponds to a **unit of work**.

Rather than rendering an entire application in one continuous operation, React can process one Fiber at a time.

A simplified mental model looks like this:

```text
Process App Fiber
↓
Process Navbar Fiber
↓
Process Sidebar Fiber
↓
Process Dashboard Fiber
```

After completing one unit of work, React can make a decision.

Should it continue rendering?

Or should it temporarily yield control back to the browser?

That decision is what enables responsive applications.

If a higher-priority update arrives, such as a user typing into an input, React can pause the current work, respond to the interaction, and resume rendering afterward.

The Stack Reconciler couldn't do this because progress existed only on the JavaScript call stack.

Fiber keeps its progress inside the Fiber tree itself, allowing React to stop and continue whenever it chooses.

This ability to interrupt and resume work became the foundation for modern React features like Concurrent Rendering and Transitions.

<Callout type="note" title="Next Sections Coming Soon">
The rest of this article is currently being drafted. We will explore:
- How React Traverses the Fiber Tree (The Linked List implementation)
- Render Phase vs Commit Phase
- Why Fiber Doesn't Make React "Faster"
- Why Fiber Still Matters Today
</Callout>
